/**
 * useCurrentUser Hook
 *
 * React Query hook for fetching and managing current user data.
 * Provides caching, refetching, and mutation capabilities.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deleteAccount,
  getCurrentUser,
  getProfileOptions,
  updateCurrentUser,
} from "../services/userService";
import type { ProfileOptions, User, UserUpdate } from "../types/user";

export function useCurrentUser() {
  return useQuery<User, Error>({
    queryKey: ["currentUser"],
    queryFn: getCurrentUser,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateCurrentUser() {
  const queryClient = useQueryClient();

  return useMutation<User, Error, UserUpdate>({
    mutationFn: updateCurrentUser,
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(["currentUser"], updatedUser);
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation<
    { message: string; summary: Record<string, unknown> },
    Error,
    string
  >({
    mutationFn: (confirmation: string) => deleteAccount(confirmation),
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function useProfileOptions() {
  return useQuery<ProfileOptions, Error>({
    queryKey: ["profileOptions"],
    queryFn: getProfileOptions,
    staleTime: Infinity, // These don't change
  });
}
